/**
 * Become a Partner form schema (COM-737) — ONE schema, run by the RVF form in
 * the browser and again by `/resources/partner-form` on the server.
 *
 * There is no legacy implementation to port: the live page
 * (`/become-a-partner.html`) is a Fastr page whose "form" is a drawn image with
 * a link hotspot over the button — no inputs, nothing posts. Field set, order
 * and copy are the COM-737 acceptance criteria as revised 2026-10-13 (First /
 * Last Name merged into Full Name, Organization type removed).
 *
 * The server-side length caps mirror `PartnerForm-Submit`'s and sit far above
 * anything a person types (AC: no character limits); they only bound what a
 * script can mail to the partnerships inbox.
 */
import { z } from "zod";

import { withZod } from "@rvf/zod";

import { phoneValidator } from "~/routes/auth/schemas/register";

/**
 * Must match `EMAIL_PATTERN` in `org_hec/cartridge/controllers/PartnerForm.js`.
 * The TLD is `[A-Za-z]{2,}`, not Contact Us's `[\w]{2,6}`: partners write from
 * long TLDs (`.insurance`, `.solutions`) that the older rule rejects.
 */
export const PARTNER_FORM_EMAIL_PATTERN = /^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/;

/** Must match `MAX_LENGTH` in `org_hec/cartridge/controllers/PartnerForm.js`. */
export const PARTNER_FORM_MAX_LENGTH = {
  fullName: 100,
  companyName: 150,
  jobTitle: 150,
  email: 254,
  message: 10_000,
} as const;

/** Ticket copy, verbatim. */
export const PARTNER_FORM_ERRORS = {
  fullName: "Please enter full name",
  companyName: "Please enter Company Name.",
  email: "Please enter valid email",
  phone: "Please enter phone number",
  tooLong: "Please shorten this entry.",
} as const;

// Trimmed, exactly as `PartnerForm-Submit` does (`readField`): a whitespace-only
// name must fail HERE with the field's copy, not reach SFRA and come back as a
// generic "try again" the shopper can never get past.
const required = (message: string, max: number) =>
  z
    .string({ required_error: message })
    .trim()
    .min(1, message)
    .max(max, PARTNER_FORM_ERRORS.tooLong);

const optional = (max: number) =>
  z.string().trim().max(max, PARTNER_FORM_ERRORS.tooLong).default("");

export const partnerFormSchema = z.object({
  fullName: required(
    PARTNER_FORM_ERRORS.fullName,
    PARTNER_FORM_MAX_LENGTH.fullName,
  ),
  companyName: required(
    PARTNER_FORM_ERRORS.companyName,
    PARTNER_FORM_MAX_LENGTH.companyName,
  ),
  jobTitle: optional(PARTNER_FORM_MAX_LENGTH.jobTitle),
  email: z
    .string({ required_error: PARTNER_FORM_ERRORS.email })
    .trim()
    .max(PARTNER_FORM_MAX_LENGTH.email, PARTNER_FORM_ERRORS.email)
    .regex(PARTNER_FORM_EMAIL_PATTERN, PARTNER_FORM_ERRORS.email),
  // Blank gets the ticket's copy; anything typed goes through the shared US
  // phone rule (the "conditional logic for phone number" the AC asks for —
  // same as registration / profile), which also normalises to bare digits.
  phone: z
    .string({ required_error: PARTNER_FORM_ERRORS.phone })
    .min(1, PARTNER_FORM_ERRORS.phone)
    .pipe(phoneValidator),
  message: optional(PARTNER_FORM_MAX_LENGTH.message),
});

export const partnerFormValidator = withZod(partnerFormSchema);
